import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

// ── Theme tokens matching SalesGro CRM ─────────────────────────────────────
const T = {
  green:       '#16A34A',   // primary action — matches sidebar active green
  greenLight:  '#DCFCE7',   // soft fill
  greenMid:    '#4ADE80',   // played bar accent
  barUnplayed: '#E2E8F0',   // unplayed bars
  barPlayed:   '#16A34A',   // played bars
  barHead:     '#4ADE80',   // playhead bar
  bg:          '#FFFFFF',
  border:      '#E8F5E9',
  textPrimary: '#111827',
  textMuted:   '#9CA3AF',
};

// ── Helpers ─────────────────────────────────────────────────────────────────
const formatTime = (s) => {
  if (!s || isNaN(s)) return '0:00';
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
};

const seeded = (seed) => {
  let s = seed;
  return () => { s = (s * 16807 + 0) % 2147483647; return (s - 1) / 2147483646; };
};

const generateBars = (url = '', count = 70) => {
  const rand = seeded(url.split('').reduce((a, c) => a + c.charCodeAt(0), 1));
  return Array.from({ length: count }, (_, i) => {
    const env  = Math.sin((i / count) * Math.PI);
    const env2 = Math.sin((i / count) * Math.PI * 2.5) * 0.3;
    return Math.max(0.05, Math.min(1, 0.08 + rand() * 0.6 * env + env2 * rand() + 0.04));
  });
};

// ── Component ────────────────────────────────────────────────────────────────
const AudioPlayer = ({ audioUrl, duration, sx = {} }) => {
  const [isPlaying, setIsPlaying]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [currentTime, setCurrentTime] = useState('0:00');
  const [volume, setVolume]           = useState(0.8);
  const [muted, setMuted]             = useState(false);
  const [bars]                        = useState(() => generateBars(audioUrl, 70));
  const audioRef = useRef(null);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => {
      const dur = el.duration || 0;
      setCurrentTime(formatTime(el.currentTime));
      setProgress(dur ? (el.currentTime / dur) * 100 : 0);
    };
    const onEnd = () => { setIsPlaying(false); setProgress(0); setCurrentTime('0:00'); };
    el.addEventListener('timeupdate', onTime);
    el.addEventListener('ended', onEnd);
    return () => { el.removeEventListener('timeupdate', onTime); el.removeEventListener('ended', onEnd); };
  }, []);

  const togglePlay = useCallback(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) { el.pause(); setIsPlaying(false); }
    else { el.play().catch(console.error); setIsPlaying(true); }
  }, [isPlaying]);

  const seekToBar = (i) => {
    const el = audioRef.current;
    if (!el || !el.duration) return;
    el.currentTime = (i / bars.length) * el.duration;
    setProgress((i / bars.length) * 100);
  };

  const toggleMute = () => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !muted;
    setMuted(!muted);
  };

  const handleVolume = (e) => {
    const val = parseFloat(e.target.value);
    if (audioRef.current) audioRef.current.volume = val;
    setVolume(val);
    if (val === 0) setMuted(true);
    else if (muted) { audioRef.current.muted = false; setMuted(false); }
  };

  const effectiveVol = muted ? 0 : volume;

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '10px',
        background: T.bg,
        border: `1px solid ${T.border}`,
        px: 2,
        pt: 1.5,
        pb: 1.5,
        ...sx,
      }}
    >
      <audio ref={audioRef} src={audioUrl} preload="metadata" />

      {/* Waveform — only rendered when playing */}
      <Box
        sx={{
          overflow: 'hidden',
          maxHeight: isPlaying ? '36px' : '0px',
          opacity: isPlaying ? 1 : 0,
          marginBottom: isPlaying ? '10px' : '0px',
          transition: 'max-height 0.3s ease, opacity 0.25s ease, margin-bottom 0.3s ease',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 36,
            gap: '1.5px',
            cursor: 'pointer',
          }}
        >
          {bars.map((h, i) => {
            const played  = (i / bars.length) * 100 <= progress;
            const isHead  = Math.abs((i / bars.length) * 100 - progress) < (100 / bars.length);
            return (
              <Box
                key={i}
                onClick={() => seekToBar(i)}
                sx={{
                  flex: 1,
                  borderRadius: '1px',
                  // Thin bars: cap at 70% height, minimum 2px
                  height: `${Math.max(8, Math.round(h * 70))}%`,
                  minHeight: '2px',
                  maxHeight: '70%',
                  alignSelf: 'center',
                  background: isHead
                    ? T.barHead
                    : played
                    ? T.barPlayed
                    : T.barUnplayed,
                  transition: 'background 0.06s',
                  ...(isPlaying && played && !isHead && {
                    animation: `wv ${(0.5 + (i % 6) * 0.07).toFixed(2)}s ease-in-out infinite alternate`,
                    animationDelay: `${((i / bars.length) * 0.3).toFixed(2)}s`,
                  }),
                  '@keyframes wv': {
                    '0%':   { transform: 'scaleY(0.7)',  opacity: 0.8 },
                    '100%': { transform: 'scaleY(1.25)', opacity: 1   },
                  },
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Controls row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>

        {/* Play / Pause button */}
        <Box
          onClick={togglePlay}
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: T.green,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            flexShrink: 0,
            color: '#fff',
            transition: 'background 0.15s, box-shadow 0.15s',
            boxShadow: isPlaying
              ? `0 0 0 3px ${T.greenLight}`
              : '0 1px 4px rgba(22,163,74,0.35)',
            '&:hover': { background: '#15803D' },
          }}
        >
          {isPlaying
            ? <PauseIcon sx={{ fontSize: 15 }} />
            : <PlayArrowIcon sx={{ fontSize: 17, ml: '1px' }} />
          }
        </Box>

        {/* Time */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
          <Typography sx={{
            fontSize: '0.72rem',
            fontWeight: 600,
            color: T.textPrimary,
            fontVariantNumeric: 'tabular-nums',
            letterSpacing: '0.01em',
          }}>
            {currentTime}
          </Typography>
          <Typography sx={{ fontSize: '0.68rem', color: T.barUnplayed, mx: 0.2 }}>/</Typography>
          <Typography sx={{
            fontSize: '0.68rem',
            fontWeight: 500,
            color: T.textMuted,
            fontVariantNumeric: 'tabular-nums',
          }}>
            {duration || '0:00'}
          </Typography>
        </Box>

        {/* Spacer */}
        <Box sx={{ flex: 1 }} />

        {/* Mute toggle */}
        <Box
          onClick={toggleMute}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            color: muted ? T.barUnplayed : T.textMuted,
            transition: 'color 0.15s',
            '&:hover': { color: T.green },
          }}
        >
          {muted
            ? <VolumeOffIcon sx={{ fontSize: 16 }} />
            : <VolumeUpIcon  sx={{ fontSize: 16 }} />
          }
        </Box>

        {/* Volume slider */}
        <Box sx={{ width: 64 }}>
          <input
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={effectiveVol}
            onChange={handleVolume}
            style={{
              width: '100%',
              height: '2px',
              borderRadius: '2px',
              appearance: 'none',
              WebkitAppearance: 'none',
              outline: 'none',
              cursor: 'pointer',
              background: `linear-gradient(to right,
                ${T.green} 0%,
                ${T.green} ${effectiveVol * 100}%,
                ${T.barUnplayed} ${effectiveVol * 100}%,
                ${T.barUnplayed} 100%)`,
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default AudioPlayer;
